import { getAppStore } from '~src/app/AppStore';
import { SessionServerSelectors,SessionSiteSelectors, SessionUserSelectors } from '~src/app/selectors/SessionInfoSelectors';
import { Formatters,Messages } from '~src/codegen/Localize';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { LinkedTaskInfo } from '~src/ts/models/LinkedTaskInfo';
import { LinkedTaskItem } from '~src/ts/ServerApi/types/LinkedTaskItem';
import { FlowRunSpecOutputStepParamsMap } from '~src/ts/ServerApi/types/RunFlowTask';
import { ToasterService, ToastType } from '~src/ts/services/ToasterService';

import { LinkedTaskDialogState, LinkedTaskFlowState, LinkedTaskItemStateType } from '../state/LinkedTaskStateTypes';
import { LinkedTaskErrors } from './LinkedTaskErrors';

export const linkedTaskInitialState: LinkedTaskDialogState = {
  selectedSchedule: '',
  items: []
};

export function LinkedTaskFlowInitialState(flowId: string, flowName: string, existingId?: string): LinkedTaskFlowState {
  return {
    linkedTaskStepId: existingId,
    flowId,
    flowName,
    outputSelection: {
      selectedOutputs: {},
      isIncrementalMap: {},
    },
    dqw: {
      isSevere: false,
      message: '',
    },
    stopRemainingTasks: true,
    dqwEnabled: false,
  };
}

// Helper function to update a value at a specific index in an array
export function updateInArray<T>(index: number, value: T, array: T[]): T[] {
  return [
    ...array.slice(0, index),
    value,
    ...array.slice(index + 1)
  ];
}
// Insert an item before or after an index in an array
export function insertInArray<T>(index: number, placement: 'before' | 'after', value: T, array: T[]): T[] {
  if (placement === 'before') {
    return [
      ...array.slice(0, index),
      value,
      ...array.slice(index)
    ];
  } else { // after
    return [
      ...array.slice(0, index + 1),
      value,
      ...array.slice(index + 1)
    ];
  }
}

export function isLinkedTaskFlowState(state: LinkedTaskItemStateType): state is LinkedTaskFlowState {
  return (state as any).flowId !== undefined;
}

// Converts front end dialog state for linked task into the linked task backend compatible object
export function convertLinkedTaskState(input: LinkedTaskItemStateType[]): LinkedTaskItem[] {
  return input.filter(item => isLinkedTaskFlowState(item)).map((item: LinkedTaskFlowState, index: number) => {
    const flowOutputStepIds: string[] = Object.keys(item.outputSelection.selectedOutputs).filter(key => item.outputSelection.selectedOutputs[key]);
    const flowRunSpecOutputStepParamsMap: FlowRunSpecOutputStepParamsMap = {};
    Object.keys(item.outputSelection.selectedOutputs).forEach(key => {
      if (item.outputSelection.selectedOutputs[key] === true) {
        flowRunSpecOutputStepParamsMap[Number(key)] = {
          isIncremental: item.outputSelection.isIncrementalMap && item.outputSelection.isIncrementalMap[key] || item.outputSelection.runAllOption === 'runAllIncremental',
          flowOutputStepId: Number(key)
        };
      }
    });
    return {
      linkedTaskStepId: item.linkedTaskStepId, // maybe null
      linkedTaskContentItem: {
        targetId: item.flowId,
        isRunAllIncremental: item.outputSelection.runAllOption === 'runAllIncremental',
        runAllOutputs: item.outputSelection.futureOutputsIncluded === undefined ? true : item.outputSelection.futureOutputsIncluded,
        flowRunSpecOutputStepParamsMap,
        flowOutputStepIds,
      },
      taskType: 'Flow',
      shouldStopRemainingTasksOnFailure: item.stopRemainingTasks,
      shouldAddDataQualityWarning: item.dqwEnabled,
      shouldEnableDataQualityWarningHighVisibility: item.dqw.isSevere,
      dataQualityWarningMessage: item.dqw.message,
      stepNumber: index + 1,
    };
  });
}

// Converts LinkedTaskInfo to Front End Linked Task Dialog State
export function convertToLinkedTaskDialogState(input: LinkedTaskInfo): LinkedTaskDialogState {
  const items: LinkedTaskItemStateType[] = input.linkedTaskItems.map(item => {
    if (item === null) {
      return {};
    }
    const selectedOutputs: { [key: string]: boolean } = {};
    const isIncrementalMap: { [key: string]: boolean } = {};
    if (item.runAllOutputs) {
      item.flowOutputSteps.forEach(step => {
        selectedOutputs[step.id] = true;
        isIncrementalMap[step.id] = item.isRunAllIncremental;
      });
    } else {
      for (const key in item.flowRunSpecOutputStepParamsMap) {
        selectedOutputs[key] = true,
        isIncrementalMap[key] = item.flowRunSpecOutputStepParamsMap[key].isIncremental;
      }
    }
    return {
      linkedTaskStepId: item.linkedTaskStepId,
      flowId: item.id,
      flowName: item.name,
      dqwEnabled: item.shouldAddDataQualityWarning,
      stopRemainingTasks: item.shouldStopRemainingTasksOnFailure,
      dqw: {
        isSevere: item.shouldEnableDataQualityWarningHighVisibility,
        message: item.dataQualityWarningMessage,
      },
      outputSelection: {
        selectedOutputs: selectedOutputs,
        isIncrementalMap: isIncrementalMap,
        futureOutputsIncluded: item.runAllOutputs,
        runAllOption: item.isRunAllIncremental ? 'runAllIncremental' : 'runAllFull',
        selectAllChecked: item.runAllOutputs,
      },
    };
  });

  return {
    linkedTaskId: input.id,
    selectedSchedule: input.schedule.id,
    items: items
  };
}


export function createTaskEnabled(state: LinkedTaskDialogState): boolean {
  if (state.selectedSchedule) {
    const linkedTaskItems = state.items.filter(item => isLinkedTaskFlowState(item));
    if (linkedTaskItems.length > 0 && linkedTaskItems.length === state.items.length) {
      return true;
    }
  }
  return false;
}

// Get the first valid flow id in a list of linked task items, otherwise return null
export function getFirstFlowId(items: LinkedTaskItemStateType[]): string {
  const firstFlowItem = items.find(item => isLinkedTaskFlowState(item));
  return firstFlowItem && isLinkedTaskFlowState(firstFlowItem) ? firstFlowItem.flowId : null;
}

export function displayLinkedTaskToasts(result: IResult, successMsg?: string) {
  if (result.errors && result.errors.length > 0) {
    ToasterService.instance.pop({
      type: ToastType.Error,
      text: LinkedTaskErrors.errorMessageFor(result.errors[0].code)
    });
  } else if (successMsg) {
    ToasterService.instance.pop({
      type: ToastType.Info,
      text: successMsg
    });
  }
}

// On if feature flag and site setting are enabled
export function areLinkedTasksEnabled(inServerArea?: boolean): boolean {
  const state = getAppStore().getState();
  const isMultiSite = SessionUserSelectors.isSessionMultiSite(state);
  const isEnabledOnServerOrOnline = SessionServerSelectors.isOnlineEnvironment(state) || SessionServerSelectors.linkedTasksSchedulingEnabled(state);
  const isEnabledOnSite = SessionSiteSelectors.isLinkedTaskSchedulingOnSiteEnabled(state);
  return isEnabledOnServerOrOnline && (inServerArea || isEnabledOnSite || !isMultiSite);
}

// Find the first flow id upstream or downstream of an index, or return null
export function findFlowIdFromLinkedTasks(items: LinkedTaskItemStateType[], index: number, direction: 'upstream' | 'downstream'): string | null {
  let arr: LinkedTaskItemStateType[];
  // Look upstream or downstream from the index
  if (direction === 'upstream') {
    arr = items.slice(0, index);
    arr.reverse();
  } else {
    arr = items.slice(index);
  }
  const flow = arr.find(item => isLinkedTaskFlowState(item));
  return flow !== undefined ? (flow as LinkedTaskFlowState).flowId : null;
}

export const maximumLinkedTasks = 20;

export function displayMaximumLinkedTaskMessage() {
  ToasterService.instance.pop({ type: ToastType.Error, text: Messages.linkedTasksMaximumTasksMessage({ MAXIMUM_COUNT_LOC: Formatters.formatCount(maximumLinkedTasks)})});
}

export function displayContentActionNotificationForLinkedTasks(): void {
  ToasterService.instance.pop({ type: ToastType.Error, text: Messages.linkedTasksRunWithFlowTasksError()});
}
